"use client";
import * as React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MoveLeft, PenLine, CheckCircle2, XCircle, Truck, Package, FileText, Wallet, RefreshCw, ClipboardCheck } from "lucide-react";
import { useERP, formatBDT, formatNum, type ID, type ERPState } from "@/lib/erp/store";
import { PrintButton, type PrintData } from "./PrintExport";
import { SignaturePad } from "./SignaturePad";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";

export type DetailType = "po" | "so" | "grn" | "invoice" | "receipt" | "rma";

export interface FullDetailPageProps {
  type: DetailType;
  id: ID;
  onBack: () => void;
  onNavigate: (pageId: string) => void;
}

export function FullDetailPage({ type, id, onBack, onNavigate }: FullDetailPageProps) {
  const erp = useERP();
  const { toast } = useToast();
  const [showSig, setShowSig] = React.useState(false);

  // register the deferred navigation callback for use inside loadDetail closures
  React.useEffect(() => { setDetailNavAfter(onNavigate); return () => setDetailNavAfter(() => {}); }, [onNavigate]);

  const data = React.useMemo(() => loadDetail(type, id, erp), [type, id, erp]);
  if (!data) {
    return (
      <div className="flex flex-col items-center gap-4 py-12">
        <p className="text-muted-foreground">Record not found.</p>
        <Button variant="outline" onClick={onBack}><MoveLeft className="size-4" /> Back</Button>
      </div>
    );
  }

  const onAction = (action: string, fn: () => void) => {
    try { fn(); toast({ title: `${action} successful`, description: `${data.title} #${data.docNumber} updated.` }); }
    catch (e) { toast({ title: `${action} failed`, description: String(e), variant: "destructive" }); }
  };

  const printData: PrintData = {
    title: data.title,
    docNumber: data.docNumber,
    docDate: data.docDate,
    meta: data.meta,
    columns: data.lineColumns.map(c => ({ key: c.key, label: c.label, align: c.align })),
    rows: data.lineRows,
    totals: data.totals,
    signatures: [{ label: "Prepared By", name: data.preparedBy }, { label: "Approved By", name: data.approver || "—" }],
    notes: data.notes,
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col gap-2">
          <Button variant="ghost" size="sm" onClick={onBack} className="w-fit -ml-2"><MoveLeft className="size-4" /> Back</Button>
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold sm:text-2xl">{data.title}</h1>
            <Badge variant={data.statusVariant}>{data.status}</Badge>
          </div>
          <p className="text-muted-foreground font-mono text-sm">{data.docNumber}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <PrintButton data={printData} label="Print" />
          <Button variant="outline" onClick={() => setShowSig(true)}><PenLine className="size-4" /> Sign</Button>
        </div>
      </div>

      {/* Meta info grid */}
      <Card>
        <CardHeader><CardTitle className="text-sm">Reference Information</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {data.meta.map((m, i) => (
              <div key={i} className="flex flex-col gap-1 border-l-2 border-muted pl-3">
                <span className="text-muted-foreground text-xs uppercase tracking-wide">{m.label}</span>
                <span className="text-sm font-medium">{m.value}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Action buttons */}
      {data.actions.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-sm">Workflow Actions</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {data.actions.map((a, i) => (
                <Button key={i} size="sm" variant={a.variant} disabled={a.disabled}
                  onClick={() => onAction(a.label, a.run)}>
                  {a.icon}{a.label}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Line items */}
      <Card>
        <CardHeader><CardTitle className="text-sm">Line Items</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="border-b">
                  {data.lineColumns.map(c => (
                    <th key={c.key} className={`px-3 py-2 text-xs font-semibold uppercase ${c.align === "right" ? "text-right" : c.align === "center" ? "text-center" : "text-left"}`}>{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.lineRows.map((r, i) => (
                  <tr key={i} className="border-b last:border-0">
                    {data.lineColumns.map(c => (
                      <td key={c.key} className={`px-3 py-2 ${c.align === "right" ? "text-right" : c.align === "center" ? "text-center" : "text-left"}`}>{r[c.key] as React.ReactNode}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
              {data.totals && data.totals.length > 0 && (
                <tfoot>
                  {data.totals.map((t, i) => (
                    <tr key={i} className="border-t bg-muted/30">
                      <td colSpan={Math.max(1, data.lineColumns.length - 1)} className="text-muted-foreground px-3 py-2 text-right text-xs uppercase">{t.label}</td>
                      <td className="px-3 py-2 text-right font-semibold">{t.value}</td>
                    </tr>
                  ))}
                </tfoot>
              )}
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Cross-references */}
      {data.crossRefs && data.crossRefs.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-sm">Linked Documents</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {data.crossRefs.map((cr, i) => (
                <Button key={i} size="sm" variant="outline" onClick={() => onNavigate(cr.nav)}>
                  {cr.icon}{cr.label} <span className="text-muted-foreground font-mono text-xs">{cr.ref}</span>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={showSig} onOpenChange={setShowSig}>
        <DialogContent>
          <DialogHeader><DialogTitle>Digital Signature</DialogTitle></DialogHeader>
          <SignaturePad onSave={() => { setShowSig(false); toast({ title: "Signature captured", description: "Document signed and stored." }); }} />
          <DialogFooter><Button variant="outline" onClick={() => setShowSig(false)}>Cancel</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface DetailData {
  title: string;
  docNumber: string;
  docDate: string;
  status: string;
  statusVariant: "default" | "secondary" | "destructive";
  meta: { label: string; value: string }[];
  lineColumns: { key: string; label: string; align?: "left" | "right" | "center" }[];
  lineRows: Record<string, React.ReactNode>[];
  totals?: { label: string; value: string }[];
  actions: { label: string; run: () => void; icon: React.ReactNode; variant?: "default" | "outline" | "destructive"; disabled?: boolean }[];
  crossRefs?: { label: string; ref: string; nav: string; icon: React.ReactNode }[];
  preparedBy: string;
  approver?: string;
  notes?: string;
}

function statusVariant(s: string): "default" | "secondary" | "destructive" {
  if (["Approved", "Received", "Delivered", "Posted", "Reconciled", "Paid", "Accepted", "Delivered", "Credit Note Issued"].includes(s)) return "default";
  if (["Rejected", "Failed", "Bounced", "Cancelled"].includes(s)) return "destructive";
  return "secondary";
}

function loadDetail(type: DetailType, id: ID, erp: ERPState): DetailData | null {
  if (type === "po") {
    const po = erp.purchaseOrders.find(p => p.id === id); if (!po) return null;
    const supplier = erp.getSupplier(po.supplierId);
    const warehouse = erp.getWarehouse(po.warehouseId);
    const grns = erp.goodsReceipts.filter(g => g.poId === id);
    return {
      title: "Purchase Order",
      docNumber: po.poNumber,
      docDate: po.orderDate,
      status: po.status,
      statusVariant: statusVariant(po.status),
      meta: [
        { label: "Supplier", value: supplier?.name ?? "—" },
        { label: "Warehouse", value: warehouse?.name ?? "—" },
        { label: "Order Date", value: po.orderDate },
        { label: "Expected Delivery", value: po.expectedDelivery },
        { label: "Created By", value: po.createdBy },
        { label: "Approved By", value: po.approvedBy ?? "—" },
        { label: "Total Value", value: formatBDT(po.total) },
        { label: "Status", value: po.status },
      ],
      lineColumns: [
        { key: "product", label: "Product" },
        { key: "qty", label: "Qty", align: "right" },
        { key: "unitPrice", label: "Unit Price", align: "right" },
        { key: "received", label: "Received", align: "right" },
        { key: "total", label: "Total", align: "right" },
      ],
      lineRows: po.lines.map(l => {
        const p = erp.getProduct(l.productId);
        return {
          product: <span><span className="font-medium">{p?.name}</span><br /><span className="text-muted-foreground font-mono text-xs">{p?.sku}</span></span>,
          qty: formatNum(l.qty),
          unitPrice: formatBDT(l.unitPrice),
          received: <span className={l.receivedQty >= l.qty ? "text-primary font-medium" : "text-destructive"}>{formatNum(l.receivedQty)}</span>,
          total: formatBDT(l.qty * l.unitPrice),
        };
      }),
      totals: [{ label: "Grand Total", value: formatBDT(po.total) }],
      actions: [
        { label: "Approve", run: () => erp.approvePO(id, "Procurement Manager"), icon: <CheckCircle2 className="size-4" />, disabled: po.status !== "Pending Approval" },
        { label: "Reject", run: () => erp.rejectPO(id, "Rejected by reviewer"), icon: <XCircle className="size-4" />, variant: "destructive", disabled: po.status !== "Pending Approval" },
        { label: "Receive (Create GRN)", run: () => {
          erp.createGRN({ poId: id, warehouseId: po.warehouseId, receivedDate: new Date().toISOString().slice(0, 10), receivedBy: "Md. Hasan",
            // M14 fix: unique batch number per line via index
            lines: po.lines.map((l, idx) => ({ productId: l.productId, expectedQty: l.qty, receivedQty: l.qty - l.receivedQty, rejectedQty: 0, batchNo: `B-${Date.now().toString().slice(-4)}-${idx + 1}` })) });
        }, icon: <Package className="size-4" />, disabled: !["Approved", "Partially Received"].includes(po.status) },
      ],
      crossRefs: grns.map(g => ({ label: "GRN", ref: g.grnNumber, nav: `grn-detail:${g.id}`, icon: <Package className="size-4" /> })),
      preparedBy: po.createdBy,
      approver: po.approvedBy,
      notes: po.notes,
    };
  }
  if (type === "so") {
    const so = erp.salesOrders.find(s => s.id === id); if (!so) return null;
    const customer = erp.getCustomer(so.customerId);
    const warehouse = erp.getWarehouse(so.warehouseId);
    const invoices = erp.salesInvoices.filter(i => i.soId === id);
    const shipments = erp.shipments.filter(s => s.soId === id);
    return {
      title: "Sales Order",
      docNumber: so.soNumber,
      docDate: so.orderDate,
      status: so.status,
      statusVariant: statusVariant(so.status),
      meta: [
        { label: "Customer", value: customer?.name ?? "—" },
        { label: "Warehouse", value: warehouse?.name ?? "—" },
        { label: "Order Date", value: so.orderDate },
        { label: "Requested Delivery", value: so.requestedDelivery },
        { label: "Created By", value: so.createdBy },
        { label: "Credit Check", value: so.creditCheck },
        { label: "Order Value", value: formatBDT(so.total) },
        { label: "Margin %", value: `${so.margin.toFixed(1)}%` },
      ],
      lineColumns: [
        { key: "product", label: "Product" },
        { key: "qty", label: "Qty", align: "right" },
        { key: "unitPrice", label: "Unit Price", align: "right" },
        { key: "discount", label: "Disc %", align: "right" },
        { key: "total", label: "Total", align: "right" },
      ],
      lineRows: so.lines.map(l => {
        const p = erp.getProduct(l.productId);
        return {
          product: <span><span className="font-medium">{p?.name}</span><br /><span className="text-muted-foreground font-mono text-xs">{p?.sku}</span></span>,
          qty: formatNum(l.qty),
          unitPrice: formatBDT(l.unitPrice),
          discount: `${l.discount}%`,
          total: formatBDT(l.qty * l.unitPrice * (1 - l.discount / 100)),
        };
      }),
      totals: [{ label: "Subtotal", value: formatBDT(so.total) }, { label: "VAT (15%)", value: formatBDT(so.total * 0.15) }, { label: "Grand Total", value: formatBDT(so.total * 1.15) }],
      actions: [
        { label: "Approve", run: () => erp.approveSO(id, "Sales Manager"), icon: <CheckCircle2 className="size-4" />, disabled: so.status !== "Pending Approval" },
        // H2 fix: pickSO throws on insufficient stock — use try/catch
        { label: "Pick", run: () => { try { erp.pickSO(id); } catch (e) { alert(String(e).replace(/^Error:\s*/, "")); } }, icon: <Package className="size-4" />, disabled: so.status !== "Approved" },
        // H5 fix: find first available vehicle (don't blindly take vehicles[0])
        { label: "Dispatch", run: () => {
          const v = erp.vehicles.find(x => x.status === "Available");
          const d = erp.employees.find(e => e.department === "Logistics" || e.designation.includes("Driver")) ?? erp.employees[0];
          if (!v) { alert("No available vehicle. Please free up a vehicle first."); return; }
          if (!d) { alert("No driver available."); return; }
          try { erp.dispatchSO(id, v.id, d.id); } catch (e) { alert(String(e).replace(/^Error:\s*/, "")); }
        }, icon: <Truck className="size-4" />, disabled: so.status !== "Picking" },
        // H6 fix: deliverSO now posts COD JE; pass signature placeholder
        { label: "Deliver", run: () => erp.deliverSO(id, 0, "Receiver Signature"), icon: <CheckCircle2 className="size-4" />, disabled: so.status !== "Dispatched" },
        // M4 fix: createInvoice now validates SO status
        { label: "Create Invoice", run: () => { try { const invId = erp.createInvoice(id); if (invId) onNavLater(`invoice-detail:${invId}`); } catch (e) { alert(String(e).replace(/^Error:\s*/, "")); } }, icon: <FileText className="size-4" />, disabled: so.status !== "Delivered" },
      ],
      crossRefs: [
        ...invoices.map(i => ({ label: "Invoice", ref: i.invoiceNumber, nav: `invoice-detail:${i.id}`, icon: <FileText className="size-4" /> })),
        ...shipments.map(s => ({ label: "Shipment", ref: s.shipmentNumber, nav: "tms-trips", icon: <Truck className="size-4" /> })),
      ],
      preparedBy: so.createdBy,
      notes: so.notes,
    };
  }
  if (type === "grn") {
    const grn = erp.goodsReceipts.find(g => g.id === id); if (!grn) return null;
    const po = erp.purchaseOrders.find(p => p.id === grn.poId);
    const warehouse = erp.getWarehouse(grn.warehouseId);
    return {
      title: "Goods Receipt Note",
      docNumber: grn.grnNumber,
      docDate: grn.receivedDate,
      status: grn.status,
      statusVariant: statusVariant(grn.status),
      meta: [
        { label: "PO Number", value: po?.poNumber ?? "—" },
        { label: "Warehouse", value: warehouse?.name ?? "—" },
        { label: "Received Date", value: grn.receivedDate },
        { label: "Received By", value: grn.receivedBy },
        { label: "Inspection Notes", value: grn.inspectionNotes ?? "—" },
        { label: "Status", value: grn.status },
      ],
      lineColumns: [
        { key: "product", label: "Product" },
        { key: "expected", label: "Expected", align: "right" },
        { key: "received", label: "Received", align: "right" },
        { key: "rejected", label: "Rejected", align: "right" },
        { key: "batch", label: "Batch No" },
      ],
      lineRows: grn.lines.map(l => {
        const p = erp.getProduct(l.productId);
        return {
          product: p?.name ?? "—",
          expected: formatNum(l.expectedQty),
          received: formatNum(l.receivedQty),
          rejected: <span className={l.rejectedQty > 0 ? "text-destructive" : ""}>{formatNum(l.rejectedQty)}</span>,
          batch: l.batchNo ?? "—",
        };
      }),
      actions: [
        { label: "Post GRN", run: () => erp.postGRN(id, grn.receivedBy), icon: <CheckCircle2 className="size-4" />, disabled: grn.status !== "Inspection" },
      ],
      crossRefs: po ? [{ label: "PO", ref: po.poNumber, nav: `po-detail:${po.id}`, icon: <FileText className="size-4" /> }] : [],
      preparedBy: grn.receivedBy,
      notes: grn.inspectionNotes,
    };
  }
  if (type === "invoice") {
    const inv = erp.salesInvoices.find(i => i.id === id); if (!inv) return null;
    const so = erp.salesOrders.find(s => s.id === inv.soId);
    const customer = erp.getCustomer(inv.customerId);
    const receipts = erp.customerReceipts.filter(r => r.allocations.some(a => a.invoiceId === id));
    return {
      title: "Sales Invoice",
      docNumber: inv.invoiceNumber,
      docDate: inv.invoiceDate,
      status: inv.status,
      statusVariant: statusVariant(inv.status),
      meta: [
        { label: "Customer", value: customer?.name ?? "—" },
        { label: "SO Number", value: so?.soNumber ?? "—" },
        { label: "Invoice Date", value: inv.invoiceDate },
        { label: "Amount", value: formatBDT(inv.amount) },
        { label: "VAT (15%)", value: formatBDT(inv.tax) },
        { label: "Total", value: formatBDT(inv.total) },
        { label: "Paid", value: formatBDT(inv.paidAmount) },
        { label: "Outstanding", value: formatBDT(inv.total - inv.paidAmount) },
      ],
      lineColumns: [
        { key: "label", label: "Item" },
        { key: "amount", label: "Amount", align: "right" },
      ],
      lineRows: [
        { label: "Sales Amount", amount: formatBDT(inv.amount) },
        { label: "VAT (15%)", amount: formatBDT(inv.tax) },
        { label: "Paid So Far", amount: formatBDT(inv.paidAmount) },
      ],
      totals: [
        { label: "Grand Total", value: formatBDT(inv.total) },
        { label: "Outstanding", value: formatBDT(inv.total - inv.paidAmount) },
      ],
      actions: [
        { label: "Post Invoice", run: () => erp.postInvoice(id, "Rumana Begum"), icon: <CheckCircle2 className="size-4" />, disabled: inv.status !== "Draft" },
        { label: "Create Receipt", run: () => { const rid = erp.createReceipt({ customerId: inv.customerId, receiptDate: new Date().toISOString().slice(0, 10), amount: inv.total - inv.paidAmount, paymentMode: "Bank Transfer", allocations: [{ invoiceId: id, amount: inv.total - inv.paidAmount }] }); onNavLater(`receipt-detail:${rid}`); }, icon: <Wallet className="size-4" />, disabled: inv.status === "Draft" || inv.status === "Paid" },
      ],
      crossRefs: [
        ...(so ? [{ label: "SO", ref: so.soNumber, nav: `so-detail:${so.id}`, icon: <FileText className="size-4" /> }] : []),
        ...receipts.map(r => ({ label: "Receipt", ref: r.receiptNumber, nav: `receipt-detail:${r.id}`, icon: <Wallet className="size-4" /> })),
      ],
      preparedBy: "Rumana Begum",
    };
  }
  if (type === "receipt") {
    const rct = erp.customerReceipts.find(r => r.id === id); if (!rct) return null;
    const customer = erp.getCustomer(rct.customerId);
    return {
      title: "Customer Receipt",
      docNumber: rct.receiptNumber,
      docDate: rct.receiptDate,
      status: rct.status,
      statusVariant: statusVariant(rct.status),
      meta: [
        { label: "Customer", value: customer?.name ?? "—" },
        { label: "Receipt Date", value: rct.receiptDate },
        { label: "Payment Mode", value: rct.paymentMode },
        { label: "Bank Ref", value: rct.bankRef ?? "—" },
        { label: "Amount", value: formatBDT(rct.amount) },
        { label: "Status", value: rct.status },
      ],
      lineColumns: [
        { key: "invoice", label: "Invoice #" },
        { key: "amount", label: "Allocated", align: "right" },
      ],
      lineRows: rct.allocations.length > 0 ? rct.allocations.map(a => {
        const inv = erp.salesInvoices.find(i => i.id === a.invoiceId);
        return { invoice: inv?.invoiceNumber ?? a.invoiceId, amount: formatBDT(a.amount) };
      }) : [{ invoice: "Unallocated", amount: formatBDT(rct.amount) }],
      totals: [{ label: "Total", value: formatBDT(rct.amount) }],
      actions: [
        { label: "Reconcile", run: () => erp.reconcileReceipt(id), icon: <CheckCircle2 className="size-4" />, disabled: rct.status !== "Pending" },
      ],
      crossRefs: rct.allocations.map(a => {
        const inv = erp.salesInvoices.find(i => i.id === a.invoiceId);
        return { label: "Invoice", ref: inv?.invoiceNumber ?? a.invoiceId, nav: `invoice-detail:${a.invoiceId}`, icon: <FileText className="size-4" /> };
      }),
      preparedBy: "Rumana Begum",
    };
  }
  if (type === "rma") {
    const rma = erp.salesReturns.find(r => r.id === id); if (!rma) return null;
    const inv = erp.salesInvoices.find(i => i.id === rma.originalInvoiceId);
    const customer = erp.getCustomer(rma.customerId);
    return {
      title: "RMA / Sales Return",
      docNumber: rma.rmaNumber,
      docDate: rma.returnDate,
      status: rma.status,
      statusVariant: statusVariant(rma.status),
      meta: [
        { label: "Original Invoice", value: inv?.invoiceNumber ?? "—" },
        { label: "Customer", value: customer?.name ?? "—" },
        { label: "Return Date", value: rma.returnDate },
        { label: "Reason", value: rma.reason },
        { label: "Credit Amount", value: formatBDT(rma.creditAmount) },
        { label: "Status", value: rma.status },
      ],
      lineColumns: [
        { key: "product", label: "Product" },
        { key: "qty", label: "Qty", align: "right" },
        { key: "condition", label: "Condition" },
      ],
      lineRows: rma.lines.map(l => {
        const p = erp.getProduct(l.productId);
        return { product: p?.name ?? "—", qty: formatNum(l.qty), condition: <Badge variant={l.condition === "Resalable" ? "default" : l.condition === "Damaged" ? "destructive" : "secondary"}>{l.condition}</Badge> };
      }),
      actions: [
        { label: "Authorize", run: () => erp.authorizeReturn(id), icon: <ClipboardCheck className="size-4" />, disabled: rma.status !== "Requested" },
        { label: "Issue Credit Note", run: () => erp.issueCreditNote(id), icon: <RefreshCw className="size-4" />, disabled: rma.status !== "Authorized" && rma.status !== "Inspected" },
      ],
      crossRefs: inv ? [{ label: "Invoice", ref: inv.invoiceNumber, nav: `invoice-detail:${inv.id}`, icon: <FileText className="size-4" /> }] : [],
      preparedBy: customer?.name ?? "—",
    };
  }
  return null;
}

// helper that pages can use to navigate after action — uses a global function via queueMicrotask to break the closure issue
let _navAfter: ((page: string) => void) | null = null;
export function setDetailNavAfter(fn: (page: string) => void) { _navAfter = fn; }
function onNavLater(page: string) { if (_navAfter) queueMicrotask(() => _navAfter?.(page)); }
